import {
  Approval,
  FactCheck,
  HighlightOff,
  MoodBad,
  LockClock,
  CheckCircleOutline,
} from '@mui/icons-material';
import { Avatar, Button, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { NotificationTypes } from '../../common/enums/enums';
import { differenceDateTime } from '../../helpers/date';

export default function NotificationCard({
  type,
  readOn,
  payload,
  onPerformAction,
}) {
  const { t } = useTranslation('global');

  const propsByAlertType = {
    [NotificationTypes.ORDER_EDITED]: {
      icon: <Approval />,
      action: t('notifications.actions.ORDER_APPROVAL'),
      message: params => (
        <Trans
          t={t}
          i18nKey="notifications.message.ORDER_UPDATED"
          values={params}
        />
      ),
    },
    [NotificationTypes.NEGATIVE_QUALITY_SURVEY]: {
      icon: <MoodBad />,
      action: t('notifications.actions.NEGATIVE_QUALITY_SURVEY'),
      message: params => (
        <Trans
          t={t}
          i18nKey="notifications.message.NEGATIVE_QUALITY_SURVEY"
          values={params}
        />
      ),
    },
    [NotificationTypes.ORDER_APPROVAL]: {
      icon: <Approval />,
      action: t('notifications.actions.ORDER_APPROVAL'),
      message: params => (
        <Trans
          t={t}
          i18nKey="notifications.message.ORDER_APPROVAL"
          values={params}
        />
      ),
    },
    [NotificationTypes.ORDER_COMPLETED]: {
      icon: <FactCheck />,
      action: t('notifications.actions.ORDER_COMPLETED'),
      message: params => (
        <Trans
          t={t}
          i18nKey="notifications.message.ORDER_COMPLETED"
          values={params}
        />
      ),
    },
    [NotificationTypes.ORDER_DECLINED]: {
      icon: <HighlightOff />,
      action: t('notifications.actions.ORDER_DECLINED'),
      message: params => (
        <Trans
          t={t}
          i18nKey="notifications.message.ORDER_DECLINED"
          values={params}
        />
      ),
    },
    [NotificationTypes.ORDER_CREATED_BY_DEALER]: {
      icon: <CheckCircleOutline />,
      action: t('notifications.actions.ORDER_CREATED_BY_DEALER'),
      message: params => (
        <Trans
          t={t}
          i18nKey="notifications.message.ORDER_CREATED_BY_DEALER"
          values={params}
        />
      ),
    },
    [NotificationTypes.ORDER_APPROVED_BY_DEALER]: {
      icon: <CheckCircleOutline />,
      action: t('notifications.actions.ORDER_APPROVED_BY_DEALER'),
      message: params => (
        <Trans
          t={t}
          i18nKey="notifications.message.ORDER_APPROVED_BY_DEALER"
          values={params}
        />
      ),
    },
    [NotificationTypes.LATE_STORE_OPENING]: {
      icon: <LockClock />,
      action: null,
      message: params => (
        <Trans
          t={t}
          i18nKey="notifications.message.LATE_STORE_OPENING"
          values={params}
        />
      ),
    },
    [NotificationTypes.ESTIMATE_APPROVAL]: {
      icon: <Approval />,
      action: t('notifications.actions.ESTIMATE_APPROVAL'),
      message: params => (
        <Trans
          t={t}
          i18nKey="notifications.message.ESTIMATE_APPROVAL"
          values={params}
        />
      ),
    },
  };

  const difference = differenceDateTime(payload.createdAt || payload.updatedAt);
  const bgColorByStatus = {
    false: 'rgba(33, 150, 243, 0.08)',
  };

  return (
    <Paper
      sx={{
        p: 1,
        pr: 2,
        boxShadow: 'none',
        bgcolor: bgColorByStatus[!!readOn],
        borderRadius: 0,
      }}
    >
      <Stack alignItems="start" gap={1}>
        <Stack direction="row" gap={2} width="100%">
          <Avatar
            sx={{
              background: theme => theme.palette.grey[200],
              color: theme => theme.palette.action.active,
            }}
          >
            {propsByAlertType[type].icon}
          </Avatar>
          <Stack
            direction="row"
            gap={2}
            alignItems="center"
            flexGrow={1}
            justifyContent="space-between"
          >
            <Typography variant="body2">
              {propsByAlertType[type].message(payload)}
            </Typography>
            <Typography
              variant="caption"
              whiteSpace="nowrap"
              alignSelf="baseline"
            >
              {(difference.days && `${Math.floor(difference.days)}d`) || ''}
              {(!difference.days &&
                difference.hours &&
                `${Math.floor(difference.hours)}h`) ||
                ''}
              {!difference.days &&
                !difference.hours &&
                difference.minutes &&
                `${Math.floor(difference.minutes)}m`}
            </Typography>
          </Stack>
        </Stack>
        {propsByAlertType[type].action && onPerformAction && (
          <Button size="small" sx={{ px: 7 }} onClick={onPerformAction}>
            {propsByAlertType[type].action}
          </Button>
        )}
      </Stack>
    </Paper>
  );
}
